import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Upload, Smartphone, Copy, ArrowRight, ArrowLeft, X, MapPin, ImagePlus, Video } from "lucide-react";
import api, { API } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES, REGIONS, LISTING_TYPES, formatPrice, imgUrl, imgLabel, AMENITIES, titlePlaceholder } from "../lib/format";

export default function PostListing() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState({ listing_fee: 1000, momo_number: "", momo_name: "" });
  const [created, setCreated] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [paid, setPaid] = useState(false);
  const [payCode, setPayCode] = useState("");
  const [payPhone, setPayPhone] = useState("");
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    title: "", category: "houses", listing_type: "sale", price: "",
    city: "", region: "Littoral", description: "", images: [],
    street: "", town_village: "", division: "", subdivision: "", gps_lat: "", gps_lng: "",
    specs: {
      land_size_sqm: "", construction_status: "constructed", living_space_sqft: "",
      bathrooms: "", bathroom_location: "inside", has_land_title: false, has_car_title: false,
      container_size_ft: "20", amenities: [],
    },
    seller_type: "", agent_type: "individual", institution_type: "bank", institution_name: "",
    video: "",
    contact_first_name: user?.name?.split(" ")[0] || "",
    contact_last_name: user?.name?.split(" ").slice(1).join(" ") || "",
    contact_phone: user?.phone || "",
    contact_whatsapp: user?.whatsapp || "", contact_email: user?.email || "",
  });
  const [imgInput, setImgInput] = useState("");
  const [gpsStatus, setGpsStatus] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addImage = () => {
    const url = imgInput.trim();
    if (url && form.images.length < 25) { set("images", [...form.images, { url, label: "" }]); setImgInput(""); }
  };
  const setLabel = (i, val) => set("images", form.images.map((im, x) => (x === i ? { ...im, label: val } : im)));
  const removeImage = (i) => set("images", form.images.filter((_, x) => x !== i));
  const setSpec = (k, v) => setForm((f) => ({ ...f, specs: { ...f.specs, [k]: v } }));

  const checkDuration = (fileObj) => new Promise((resolve) => {
    const url = URL.createObjectURL(fileObj);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(v.duration); };
    v.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
    v.src = url;
  });

  const handleVideo = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setError("");
    const duration = await checkDuration(f);
    if (duration && duration > 122) {
      setError(t("video_too_long"));
      if (videoRef.current) videoRef.current.value = "";
      return;
    }
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/upload-video", fd);
      set("video", `${API}/files/${res.data.path}`);
    } catch (err) {
      setError(err.response?.data?.detail || "Video upload failed");
    } finally {
      setUploadingVideo(false);
      if (videoRef.current) videoRef.current.value = "";
    }
  };
  const toggleAmenity = (k) => setForm((f) => {
    const a = f.specs.amenities || [];
    return { ...f, specs: { ...f.specs, amenities: a.includes(k) ? a.filter((x) => x !== k) : [...a, k] } };
  });

  const handleFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const remaining = 25 - form.images.length;
    const toUpload = picked.slice(0, remaining);
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      toUpload.forEach((f) => fd.append("files", f));
      const res = await api.post("/upload", fd);
      const added = (res.data.files || []).map((f) => ({ url: `${API}/files/${f.path}`, label: "" }));
      set("images", [...form.images, ...added]);
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const needsLocation = form.category === "land" || form.category === "houses";
  const locationOk = !needsLocation || (form.street && form.town_village && form.division && form.subdivision);

  const missingStep1 = () => {
    const m = [];
    if (!form.title) m.push(t("f_title"));
    if (!form.price) m.push(t("f_price"));
    if (!form.city) m.push(t("f_city"));
    if (!form.description) m.push(t("f_desc"));
    if (needsLocation) {
      if (!form.street) m.push(t("f_street"));
      if (!form.town_village) m.push(t("f_town"));
      if (!form.division) m.push(t("f_division"));
      if (!form.subdivision) m.push(t("f_subdivision"));
    }
    if (!form.seller_type) m.push(t("f_seller_type"));
    else if (form.seller_type === "agent" && form.agent_type === "institution" && !form.institution_name) m.push(t("f_institution_name"));
    if (!form.contact_first_name) m.push(t("f_first_name"));
    if (!form.contact_last_name) m.push(t("f_last_name"));
    if (!form.contact_phone) m.push(t("f_phone"));
    if (!form.contact_email) m.push(t("f_email"));
    return m;
  };

  const goStep2 = () => {
    const m = missingStep1();
    if (m.length) {
      setError(`${t("fill_required")}: ${m.join(", ")}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setError("");
    setStep(2);
  };

  const captureGps = () => {
    if (!navigator.geolocation) { setGpsStatus("unavailable"); return; }
    setGpsStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, gps_lat: pos.coords.latitude.toFixed(6), gps_lng: pos.coords.longitude.toFixed(6) }));
        setGpsStatus("done");
      },
      () => setGpsStatus("error"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const validStep1 = form.title && form.price && form.city && form.description && form.seller_type && form.contact_first_name && form.contact_last_name && form.contact_phone && form.contact_email && locationOk;

  const submitListing = async () => {
    setError("");
    setSubmitting(true);
    try {
      const s = form.specs || {};
      let specs = {};
      if (form.category === "houses" || form.category === "land") {
        if (s.land_size_sqm) specs.land_size_sqm = Number(s.land_size_sqm);
        specs.has_land_title = !!s.has_land_title;
        if (Array.isArray(s.amenities) && s.amenities.length) specs.amenities = s.amenities;
      }
      if (form.category === "houses") {
        specs.construction_status = s.construction_status || "constructed";
        if (s.living_space_sqft) specs.living_space_sqft = Number(s.living_space_sqft);
        if (s.bathrooms) specs.bathrooms = Number(s.bathrooms);
        specs.bathroom_location = s.bathroom_location || "inside";
      }
      if (form.category === "cars") {
        specs.has_car_title = !!s.has_car_title;
        if (s.car_make) specs.car_make = s.car_make;
        if (s.car_model) specs.car_model = s.car_model;
        if (s.car_year) specs.year = Number(s.car_year);
        if (s.fuel) specs.fuel = s.fuel;
        if (s.fuel_description) specs.fuel_description = s.fuel_description;
        if (s.mileage) {
          if (s.mileage_unit === "miles") specs.mileage_mi = Number(s.mileage);
          else specs.mileage_km = Number(s.mileage);
        }
      }
      if (form.category === "containers") {
        if (s.container_size_ft) specs.container_size_ft = Number(s.container_size_ft);
      }
      const isAgent = form.seller_type === "agent";
      const isInstitution = isAgent && form.agent_type === "institution";
      const payload = { ...form, specs, price: parseInt(form.price, 10), currency: "XAF",
        agent_type: isAgent ? form.agent_type : null,
        institution_type: isInstitution ? form.institution_type : null,
        institution_name: isInstitution ? form.institution_name : null,
        gps_lat: form.gps_lat ? parseFloat(form.gps_lat) : null,
        gps_lng: form.gps_lng ? parseFloat(form.gps_lng) : null };
      const res = await api.post("/listings", payload);
      setCreated(res.data.listing);
      setStep(3);
    } catch (e) {
      setError(e.response?.data?.detail || "Error creating listing");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmPayment = async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await api.post(`/listings/${created.id}/pay`, { phone: payPhone, reference: created.id });
      setPayCode(res.data.confirmation_code || "");
      setPaid(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [t("step_details"), t("step_media"), t("step_payment")];

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 pt-24 pb-16">
      <h1 className="font-display text-3xl md:text-4xl text-white mb-2">{t("post_title")}</h1>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-10 mt-6">
        {steps.map((label, i) => {
          const n = i + 1;
          const done = step > n;
          const active = step === n;
          return (
            <React.Fragment key={i}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 flex items-center justify-center border text-sm ${done ? "bg-gold border-gold text-black" : active ? "border-gold text-gold" : "border-white/20 text-white/40"}`}>
                  {done ? <Check size={16} /> : n}
                </div>
                <span className={`text-sm hidden sm:block ${active ? "text-white" : "text-white/40"}`}>{label}</span>
              </div>
              {i < steps.length - 1 && <div className={`flex-1 h-px ${step > n ? "bg-gold" : "bg-white/15"}`} />}
            </React.Fragment>
          );
        })}
      </div>

      {error && <div className="mb-6 bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-3 text-sm" data-testid="post-error">{error}</div>}

      {/* Step 1: Details */}
      {step === 1 && (
        <div className="space-y-6 fade-up" data-testid="step-details">
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label={t("f_category")}>
              <select data-testid="input-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c[lang]}</option>)}
              </select>
            </Field>
            <Field label={t("f_type")}>
              <select data-testid="input-type" value={form.listing_type} onChange={(e) => set("listing_type", e.target.value)} className={inputCls}>
                {LISTING_TYPES.map((ty) => <option key={ty.key} value={ty.key}>{ty[lang]}</option>)}
              </select>
            </Field>
          </div>
          <Field label={t("f_title")}>
            <input data-testid="input-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} placeholder={titlePlaceholder(form.category, lang)} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label={t("f_price")}>
              <input
                data-testid="input-price"
                type="text"
                inputMode="numeric"
                value={form.price ? Number(form.price).toLocaleString("en-US").replace(/,/g, "\u2003") : ""}
                onChange={(e) => set("price", e.target.value.replace(/\D/g, ""))}
                className={inputCls}
                placeholder="0"
              />
              {form.price ? (
                <p className="text-gold text-sm mt-1.5 font-display" data-testid="price-preview">
                  {Number(form.price).toLocaleString("en-US").replace(/,/g, "\u2003")} FCFA
                </p>
              ) : null}
            </Field>
            <Field label={t("f_region")}>
              <select data-testid="input-region" value={form.region} onChange={(e) => set("region", e.target.value)} className={inputCls}>
                {REGIONS.map((r) => <option key={r.value} value={r.value}>{r[lang]}</option>)}
              </select>
            </Field>
          </div>
          <Field label={t("f_city")}>
            <input data-testid="input-city" value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} placeholder={lang === "en" ? "e.g. Bonapriso, Douala" : "ex. Bonapriso, Douala"} />
          </Field>

          {/* Precise location block */}
          <div className="border border-white/10 bg-black/20 p-5" data-testid="location-block">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-white/90 font-medium flex items-center gap-2">
                {t("loc_section")}
                {needsLocation && <span className="text-[10px] uppercase tracking-wider text-gold border border-gold/40 px-2 py-0.5">{t("loc_note")}</span>}
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label={t("f_street")}>
                <input data-testid="input-street" value={form.street} onChange={(e) => set("street", e.target.value)} className={inputCls} placeholder={lang === "en" ? "e.g. Rue Joffre, near Total station" : "ex. Rue Joffre, près station Total"} />
              </Field>
              <Field label={t("f_town")}>
                <input data-testid="input-town" value={form.town_village} onChange={(e) => set("town_village", e.target.value)} className={inputCls} placeholder={lang === "en" ? "Town or village" : "Ville ou village"} />
              </Field>
              <Field label={t("f_division")}>
                <input data-testid="input-division" value={form.division} onChange={(e) => set("division", e.target.value)} className={inputCls} placeholder={lang === "en" ? "e.g. Wouri" : "ex. Wouri"} />
              </Field>
              <Field label={t("f_subdivision")}>
                <input data-testid="input-subdivision" value={form.subdivision} onChange={(e) => set("subdivision", e.target.value)} className={inputCls} placeholder={lang === "en" ? "e.g. Douala III" : "ex. Douala III"} />
              </Field>
            </div>
            <div className="mt-4">
              <label className="block text-white/70 text-sm mb-2">{t("f_gps")}</label>
              <div className="flex flex-wrap gap-2 items-center">
                <input data-testid="input-gps-lat" value={form.gps_lat} onChange={(e) => set("gps_lat", e.target.value)} className={`${inputCls} sm:w-40`} placeholder="Lat (4.0511)" />
                <input data-testid="input-gps-lng" value={form.gps_lng} onChange={(e) => set("gps_lng", e.target.value)} className={`${inputCls} sm:w-40`} placeholder="Lng (9.7679)" />
                <button type="button" data-testid="capture-gps-btn" onClick={captureGps} className="flex items-center gap-2 border border-white/20 text-white px-4 py-3 hover:border-gold transition-colors text-sm">
                  <MapPin size={16} /> {t("use_gps")}
                </button>
                {gpsStatus === "done" && <span className="text-green-400 text-sm">{t("gps_captured")} ✓</span>}
                {gpsStatus === "loading" && <span className="text-white/50 text-sm">…</span>}
                {(gpsStatus === "error" || gpsStatus === "unavailable") && <span className="text-red-400 text-sm">GPS unavailable</span>}
              </div>
              <p className="text-gold/80 text-xs mt-2 flex items-start gap-1.5" data-testid="gps-advice"><MapPin size={13} className="mt-0.5 shrink-0" /> {t("gps_advice")}</p>
            </div>
          </div>

          {/* Property details (category-aware) */}
          {(form.category === "houses" || form.category === "land" || form.category === "cars" || form.category === "containers") && (
            <div className="border border-white/10 bg-black/20 p-5 space-y-6" data-testid="specs-block">
              <h3 className="text-white/90 font-medium">{t("specs_section")}</h3>
              {(form.category === "houses" || form.category === "land") && (
                <div className="grid sm:grid-cols-2 gap-6 items-end">
                  <Field label={t("f_land_size")}>
                    <input data-testid="spec-land-size" type="number" value={form.specs.land_size_sqm} onChange={(e) => setSpec("land_size_sqm", e.target.value)} className={inputCls} placeholder="m²" />
                  </Field>
                  <label className="flex items-center gap-3 py-3 cursor-pointer">
                    <input type="checkbox" data-testid="spec-land-title" checked={!!form.specs.has_land_title} onChange={(e) => setSpec("has_land_title", e.target.checked)} className="accent-[#D4AF37] w-4 h-4" />
                    <span className="text-white/80 text-sm">{t("f_land_title")}</span>
                  </label>
                </div>
              )}
              {form.category === "houses" && (
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label={t("f_construction")}>
                    <select data-testid="spec-construction" value={form.specs.construction_status} onChange={(e) => setSpec("construction_status", e.target.value)} className={inputCls}>
                      <option value="constructed">{t("constructed")}</option>
                      <option value="under_construction">{t("under_construction")}</option>
                    </select>
                  </Field>
                  <Field label={t("f_living_space")}>
                    <input data-testid="spec-living-space" type="number" value={form.specs.living_space_sqft} onChange={(e) => setSpec("living_space_sqft", e.target.value)} className={inputCls} placeholder="sq ft" />
                  </Field>
                  <Field label={t("f_bathrooms")}>
                    <input data-testid="spec-bathrooms" type="number" value={form.specs.bathrooms} onChange={(e) => setSpec("bathrooms", e.target.value)} className={inputCls} />
                  </Field>
                  <Field label={t("f_bath_location")}>
                    <select data-testid="spec-bath-location" value={form.specs.bathroom_location} onChange={(e) => setSpec("bathroom_location", e.target.value)} className={inputCls}>
                      <option value="inside">{t("inside")}</option>
                      <option value="outside">{t("outside")}</option>
                    </select>
                  </Field>
                </div>
              )}
              {(form.category === "houses" || form.category === "land") && (
                <div>
                  <label className="block text-white/70 text-sm mb-3">{t("amenities_title")}</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {AMENITIES.map((a) => {
                      const on = form.specs.amenities?.includes(a.key);
                      return (
                        <label key={a.key} data-testid={`amenity-${a.key}`} className={`flex items-center gap-2 border px-3 py-2.5 cursor-pointer transition-colors ${on ? "border-gold text-white" : "border-white/15 text-white/60 hover:border-white/40"}`}>
                          <input type="checkbox" checked={on || false} onChange={() => toggleAmenity(a.key)} className="accent-[#D4AF37] w-4 h-4" />
                          <span className="text-sm">{a[lang]}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              {form.category === "cars" && (
                <div className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label={t("f_car_make")}>
                      <input data-testid="spec-car-make" value={form.specs.car_make} onChange={(e) => setSpec("car_make", e.target.value)} className={inputCls} placeholder={lang === "en" ? "e.g. Toyota" : "ex. Toyota"} />
                    </Field>
                    <Field label={t("f_car_model")}>
                      <input data-testid="spec-car-model" value={form.specs.car_model} onChange={(e) => setSpec("car_model", e.target.value)} className={inputCls} placeholder={lang === "en" ? "e.g. Land Cruiser" : "ex. Land Cruiser"} />
                    </Field>
                    <Field label={t("f_year")}>
                      <input data-testid="spec-car-year" type="number" value={form.specs.car_year} onChange={(e) => setSpec("car_year", e.target.value)} className={inputCls} placeholder="2020" />
                    </Field>
                    <Field label={t("f_fuel")}>
                      <select data-testid="spec-fuel" value={form.specs.fuel} onChange={(e) => setSpec("fuel", e.target.value)} className={inputCls}>
                        <option value="petrol">{t("fuel_petrol")}</option>
                        <option value="diesel">{t("fuel_diesel")}</option>
                        <option value="electric">{t("fuel_electric")}</option>
                        <option value="other">{t("fuel_other")}</option>
                      </select>
                    </Field>
                  </div>
                  <Field label={t("f_mileage")}>
                    <div className="flex gap-2">
                      <input data-testid="spec-mileage" type="number" value={form.specs.mileage} onChange={(e) => setSpec("mileage", e.target.value)} className={`${inputCls} flex-1`} placeholder="0" />
                      <select data-testid="spec-mileage-unit" value={form.specs.mileage_unit} onChange={(e) => setSpec("mileage_unit", e.target.value)} className={`${inputCls} w-28`}>
                        <option value="km">km</option>
                        <option value="miles">miles</option>
                      </select>
                    </div>
                  </Field>
                  {form.specs.fuel === "other" && (
                    <Field label={t("f_fuel_desc")}>
                      <input data-testid="spec-fuel-desc" value={form.specs.fuel_description} onChange={(e) => setSpec("fuel_description", e.target.value)} className={inputCls} placeholder={lang === "en" ? "e.g. Hybrid, LPG..." : "ex. Hybride, GPL..."} />
                    </Field>
                  )}
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" data-testid="spec-car-title" checked={!!form.specs.has_car_title} onChange={(e) => setSpec("has_car_title", e.target.checked)} className="accent-[#D4AF37] w-4 h-4" />
                    <span className="text-white/80 text-sm">{t("f_car_title")}</span>
                  </label>
                </div>
              )}
              {form.category === "containers" && (
                <Field label={t("f_container_size")}>
                  <select data-testid="spec-container-size" value={form.specs.container_size_ft} onChange={(e) => setSpec("container_size_ft", e.target.value)} className={inputCls}>
                    <option value="10">10 ft</option>
                    <option value="20">20 ft</option>
                    <option value="40">40 ft</option>
                    <option value="45">45 ft</option>
                  </select>
                </Field>
              )}
            </div>
          )}

          <Field label={t("f_desc")}>
            <textarea data-testid="input-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className={inputCls} />
          </Field>

          <div className="border-t border-white/10 pt-6 space-y-6">
            <Field label={t("f_seller_type")}>
              <div className="flex gap-3">
                {[{ k: "landlord", label: t("seller_landlord") }, { k: "agent", label: t("seller_agent") }].map((o) => (
                  <button
                    key={o.k}
                    type="button"
                    data-testid={`seller-type-${o.k}`}
                    onClick={() => set("seller_type", o.k)}
                    className={`flex-1 py-3 text-sm border transition-colors ${form.seller_type === o.k ? "bg-gold text-black border-gold" : "border-white/15 text-white/70 hover:border-white/40"}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </Field>
            {form.seller_type === "agent" && (
              <div className="space-y-6 border-l-2 border-gold/30 pl-4" data-testid="agent-type-block">
                <Field label={t("f_agent_type")}>
                  <div className="flex gap-3">
                    {[{ k: "individual", label: t("agent_individual") }, { k: "institution", label: t("agent_institution") }].map((o) => (
                      <button
                        key={o.k}
                        type="button"
                        data-testid={`agent-type-${o.k}`}
                        onClick={() => set("agent_type", o.k)}
                        className={`flex-1 py-2.5 text-sm border transition-colors ${form.agent_type === o.k ? "bg-gold text-black border-gold" : "border-white/15 text-white/70 hover:border-white/40"}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </Field>
                {form.agent_type === "institution" && (
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label={t("f_institution_type")}>
                      <select data-testid="input-institution-type" value={form.institution_type} onChange={(e) => set("institution_type", e.target.value)} className={inputCls}>
                        <option value="bank">{t("inst_bank")}</option>
                        <option value="credit_union">{t("inst_credit_union")}</option>
                        <option value="church">{t("inst_church")}</option>
                        <option value="non_profit">{t("inst_non_profit")}</option>
                        <option value="other">{t("inst_other")}</option>
                      </select>
                    </Field>
                    <Field label={t("f_institution_name")}>
                      <input data-testid="input-institution-name" value={form.institution_name} onChange={(e) => set("institution_name", e.target.value)} className={inputCls} placeholder={lang === "en" ? "e.g. Afriland First Bank" : "ex. Afriland First Bank"} />
                    </Field>
                  </div>
                )}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label={form.seller_type === "agent" && form.agent_type === "institution" ? `${t("f_contact_person")} — ${t("f_first_name")}` : t("f_first_name")}>
                <input data-testid="input-first-name" value={form.contact_first_name} onChange={(e) => set("contact_first_name", e.target.value)} className={inputCls} />
              </Field>
              <Field label={t("f_last_name")}>
                <input data-testid="input-last-name" value={form.contact_last_name} onChange={(e) => set("contact_last_name", e.target.value)} className={inputCls} />
              </Field>
              <Field label={t("f_phone")}>
                <input data-testid="input-contact-phone" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className={inputCls} placeholder="+237 6XX XX XX XX" />
              </Field>
              <Field label={t("f_whatsapp")}>
                <input data-testid="input-contact-whatsapp" value={form.contact_whatsapp} onChange={(e) => set("contact_whatsapp", e.target.value)} className={inputCls} placeholder="+237 6XX XX XX XX" />
              </Field>
              <Field label={t("f_email")}>
                <input data-testid="input-contact-email" type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className={inputCls} />
              </Field>
            </div>
          </div>

          <div className="flex justify-end">
            <button data-testid="step1-next" onClick={goStep2} className="bg-gold text-black px-7 py-3 font-medium hover:bg-gold-light transition-colors inline-flex items-center gap-2">
              {t("next")} <ArrowRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Media */}
      {step === 2 && (
        <div className="space-y-6 fade-up" data-testid="step-media">
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" data-testid="file-input" />

          <button
            type="button"
            data-testid="upload-device-btn"
            onClick={() => fileRef.current && fileRef.current.click()}
            disabled={uploading || form.images.length >= 25}
            className="w-full border border-dashed border-white/25 hover:border-gold bg-black/20 py-10 flex flex-col items-center justify-center gap-3 transition-colors disabled:opacity-50"
          >
            <ImagePlus size={30} strokeWidth={1.4} className="text-gold" />
            <span className="text-white font-medium">{uploading ? t("uploading") : t("upload_device")}</span>
            <span className="text-white/40 text-sm">{t("upload_hint")} · {form.images.length}/25</span>
          </button>

          <div className="flex gap-2 items-center">
            <input data-testid="input-image-url" value={imgInput} onChange={(e) => setImgInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addImage())} className={inputCls} placeholder={`${t("or_url")} (https://...)`} />
            <button type="button" data-testid="add-image-btn" onClick={addImage} className="bg-white/10 text-white px-5 py-3 hover:bg-white/20 transition-colors flex items-center gap-2 shrink-0"><Upload size={16} /> {t("add")}</button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {form.images.map((im, i) => (
              <div key={i} className="border border-white/10 bg-[#121212]" data-testid={`image-item-${i}`}>
                <div className="relative aspect-[4/3] overflow-hidden group">
                  <img src={imgUrl(im)} alt="" className="w-full h-full object-cover" />
                  {i === 0 && <span className="absolute top-2 left-2 bg-gold text-black text-[10px] px-2 py-0.5 uppercase">{t("cover")}</span>}
                  <button type="button" data-testid={`remove-image-${i}`} onClick={() => removeImage(i)} className="absolute top-2 right-2 w-7 h-7 bg-black/70 flex items-center justify-center hover:bg-red-500 transition-colors"><X size={15} className="text-white" /></button>
                </div>
                <input
                  data-testid={`image-label-${i}`}
                  value={imgLabel(im)}
                  onChange={(e) => setLabel(i, e.target.value)}
                  placeholder={t("photo_label")}
                  className="w-full bg-transparent border-t border-white/10 px-3 py-2 text-sm text-white outline-none focus:bg-black/30 placeholder-white/30"
                />
              </div>
            ))}
          </div>

          {/* Video upload (optional, max 45s) */}
          <div className="border-t border-white/10 pt-6">
            <input ref={videoRef} type="file" accept="video/*" onChange={handleVideo} className="hidden" data-testid="video-input" />
            {form.video ? (
              <div className="relative" data-testid="video-preview">
                <video src={form.video} controls className="w-full max-h-72 bg-black border border-white/10" />
                <button type="button" data-testid="remove-video-btn" onClick={() => set("video", "")} className="absolute top-2 right-2 flex items-center gap-1.5 bg-black/70 text-white text-xs px-3 py-1.5 hover:bg-red-500 transition-colors">
                  <X size={14} /> {t("remove")}
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="upload-video-btn"
                onClick={() => videoRef.current && videoRef.current.click()}
                disabled={uploadingVideo}
                className="w-full border border-dashed border-white/25 hover:border-gold bg-black/20 py-8 flex flex-col items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                <Video size={26} strokeWidth={1.4} className="text-gold" />
                <span className="text-white font-medium">{uploadingVideo ? t("uploading_video") : t("upload_video")}</span>
                <span className="text-white/40 text-sm">{t("video_hint")}</span>
              </button>
            )}
          </div>

          <div className="flex justify-between pt-4">
            <button data-testid="step2-back" onClick={() => setStep(1)} className="border border-white/20 text-white px-6 py-3 hover:border-white/50 transition-colors inline-flex items-center gap-2"><ArrowLeft size={18} /> {t("back")}</button>
            <button data-testid="step2-next" disabled={submitting || uploading || uploadingVideo || form.images.length === 0} onClick={submitListing} className="bg-gold text-black px-7 py-3 font-medium hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2">
              {submitting ? (lang === "en" ? "Creating..." : "Création...") : t("next")} <ArrowRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Payment */}
      {step === 3 && created && (
        <div className="fade-up" data-testid="step-payment">
          {!paid ? (
            <div className="bg-[#121212] border border-white/10 p-8">
              <div className="flex items-center gap-3 mb-2">
                <Smartphone className="text-gold" />
                <h2 className="font-display text-2xl text-white">{t("pay_title")}</h2>
              </div>
              <p className="text-white/50 text-sm mb-6">{t("pay_desc")}</p>

              <div className="bg-black/40 border border-white/10 p-5 space-y-4">
                <Row label={lang === "en" ? "Listing fee" : "Frais d'annonce"} value={formatPrice(config.listing_fee)} highlight />
                <Row label={lang === "en" ? "Pay to (MTN MoMo)" : "Payer à (MTN MoMo)"} value={config.momo_number} copy />
                {config.momo_name ? <Row label={lang === "en" ? "Account name" : "Nom du compte"} value={config.momo_name} /> : null}
                <Row label={lang === "en" ? "Reference" : "Référence"} value={created.id} copy />
              </div>

              <div className="mt-6 text-sm text-white/60 leading-relaxed bg-gold/5 border border-gold/20 p-4">
                {lang === "en" ? (
                  <>Send <strong className="text-white">{formatPrice(config.listing_fee)}</strong> via MTN Mobile Money to this number (<strong className="text-white">{config.momo_number}</strong>){config.momo_name ? <> <strong className="text-white">{config.momo_name}</strong></> : null}, using the reference. Then confirm below — our team verifies the payment and publishes your listing.</>
                ) : (
                  <>Envoyez <strong className="text-white">{formatPrice(config.listing_fee)}</strong> via MTN Mobile Money à ce numéro (<strong className="text-white">{config.momo_number}</strong>){config.momo_name ? <> <strong className="text-white">{config.momo_name}</strong></> : null}, avec la référence. Confirmez ensuite — notre équipe vérifie le paiement et publie votre annonce.</>
                )}
              </div>

              <Field label={t("pay_phone")} className="mt-6">
                <input data-testid="input-pay-phone" value={payPhone} onChange={(e) => setPayPhone(e.target.value)} className={inputCls} placeholder="+237 6XX XX XX XX" />
              </Field>

              <button
                data-testid="confirm-payment-btn"
                disabled={submitting || !payPhone}
                onClick={confirmPayment}
                className="w-full mt-6 bg-gold text-black py-3.5 font-medium hover:bg-gold-light disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {submitting ? t("paying") : (lang === "en" ? "I have paid — Submit for review" : "J'ai payé — Soumettre")}
              </button>
            </div>
          ) : (
            <div className="bg-[#121212] border border-white/10 p-10 text-center" data-testid="payment-success">
              <div className="w-16 h-16 rounded-full bg-green-500/15 border border-green-500/40 flex items-center justify-center mx-auto mb-5">
                <Check size={30} className="text-green-400" />
              </div>
              <h2 className="font-display text-2xl text-white mb-2">{lang === "en" ? "Submitted for review" : "Soumis pour révision"}</h2>
              {payCode && (
                <div className="max-w-sm mx-auto my-6 bg-black/40 border border-gold/30 p-5" data-testid="confirmation-code">
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">{t("confirmation_code")}</p>
                  <p className="font-display text-gold text-4xl tracking-[0.3em]">{payCode}</p>
                  <p className="text-white/50 text-xs mt-3 leading-relaxed">{t("code_note")}</p>
                </div>
              )}
              <p className="text-white/50 max-w-md mx-auto mb-8">
                {lang === "en"
                  ? "Thanks! Once we confirm your MoMo payment, your listing goes live. You can track its status in your dashboard."
                  : "Merci ! Dès confirmation de votre paiement MoMo, votre annonce sera publiée. Suivez son statut dans votre tableau de bord."}
              </p>
              <button onClick={() => navigate("/dashboard")} data-testid="go-dashboard-btn" className="bg-gold text-black px-7 py-3 font-medium hover:bg-gold-light transition-colors">{t("nav_dashboard")}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-black/40 border border-white/15 px-4 py-3 text-white outline-none focus:border-gold transition-colors placeholder-white/30";

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-white/70 text-sm mb-2">{label}</label>
      {children}
    </div>
  );
}

function Row({ label, value, highlight, copy }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-white/50 text-sm">{label}</span>
      <span className={`flex items-center gap-2 ${highlight ? "text-gold font-display text-lg" : "text-white"}`}>
        {value}
        {copy && <button onClick={() => navigator.clipboard.writeText(value)} className="text-white/40 hover:text-gold"><Copy size={14} /></button>}
      </span>
    </div>
  );
}
